import { Injectable, signal } from '@angular/core';
import { Observable, of, delay, tap } from 'rxjs';

import { ActionExecutionState, ResponseActionDefinition } from '../models/ticket.models';

@Injectable({
  providedIn: 'root',
})
export class ResponseActionsService {
  /**
   * Client-side execution state per action, keyed by "ticketId:actionLabel".
   * Survives case re-selection within the same session.
   */
  public executionStates = signal<Map<string, ActionExecutionState>>(new Map());

  /**
   * Execute an automated response action (mocked).
   * Sets state to 'pending' immediately, then 'completed' or 'failed' after delay.
   */
  public executeAction(ticketId: string, action: ResponseActionDefinition): Observable<{ success: boolean }> {
    const key = this.buildKey(ticketId, action.name);
    this.setState(key, { status: 'pending' });

    // Mock: simulate 1.5s latency then success
    return of({ success: true }).pipe(
      delay(1500),
      tap({
        next: (res) => {
          if (res.success) {
            this.setState(key, { status: 'completed' });
          } else {
            this.setState(key, { status: 'failed', error: 'Action failed' });
          }
        },
        error: () => {
          this.setState(key, { status: 'failed', error: 'Network error' });
        },
      }),
    );
  }

  /**
   * Request the SOC to perform an action via Zoho Desk reply (mocked).
   * Composes a message and "sends" it as a ticket comment.
   */
  public requestSoc(ticketId: string, actionLabel: string, hostname: string | null): Observable<{ success: boolean }> {
    const key = this.buildKey(ticketId, actionLabel);
    this.setState(key, { status: 'pending' });

    const message = hostname
      ? `Customer requests: ${actionLabel} on ${hostname}`
      : `Customer requests: ${actionLabel}`;

    // Mock: simulate Zoho Desk reply API call
    console.log(`[Mock Zoho Reply] Ticket ${ticketId}: "${message}"`);

    return of({ success: true }).pipe(
      delay(800),
      tap({
        next: (res) => {
          if (res.success) {
            this.setState(key, { status: 'soc_requested' });
          } else {
            this.setState(key, { status: 'failed', error: 'Failed to send request' });
          }
        },
        error: () => {
          this.setState(key, { status: 'failed', error: 'Network error' });
        },
      }),
    );
  }

  /**
   * Get the current execution state for an action.
   */
  public getExecutionState(ticketId: string, actionLabel: string): ActionExecutionState {
    return this.executionStates().get(this.buildKey(ticketId, actionLabel)) ?? { status: 'idle' };
  }

  /**
   * Reset execution state for a specific action (e.g. for retry).
   */
  public resetState(ticketId: string, actionLabel: string): void {
    this.setState(this.buildKey(ticketId, actionLabel), { status: 'idle' });
  }

  private buildKey(ticketId: string, actionLabel: string): string {
    return `${ticketId}:${actionLabel}`;
  }

  private setState(key: string, state: ActionExecutionState): void {
    this.executionStates.update((map) => {
      const next = new Map(map);
      next.set(key, state);
      return next;
    });
  }
}
